import React from 'react';
import {Button, Skeleton, Stack, Typography, useTheme} from '@mui/material';

import {Loading} from 'presentation/components/Loading/Loading';
import {StyledCard} from 'presentation/components';
import {translations} from 'presentation/translations-new';
import {formatPriceBill} from 'presentation/utils';

import {AutoPaymentCard} from '../AutoPaymentCard';
import type {PaymentSummaryCardsProps} from './types';
import {OpenBillStatusEnum} from '../OpenBillsTable/types';

const {totalSelectedBills, pay} = translations['pt-br'].privateFlow.myFinancial.openBills;

function PaymentSummaryCards({
	isLoading,
	isMobile,
	rows,
	selectedIds,
	isPayLoading,
	onPay,
	autoPaymentType,
	onAutoPaymentEnable,
	onAutoPaymentChange,
	onAutoPaymentDeactivate,
	isAutoPaymentLoading,
}: PaymentSummaryCardsProps) {
	const {palette} = useTheme();

	const total = rows
		.filter(row => selectedIds.has(row.cdObrigacao))
		.reduce(
			(acc, row) =>
				acc +
				(row.status === OpenBillStatusEnum.OVERDUE
					? (row.vlAtualizado ?? row.vlObrigacao)
					: row.vlObrigacao),
			0,
		);
	const totalSelectedFormatted = formatPriceBill(total);
	const showAsterisk = rows.some(
		row => selectedIds.has(row.cdObrigacao) && row.status === OpenBillStatusEnum.OVERDUE,
	);

	if (isLoading) {
		return (
			<Stack direction="row" gap={2} flex={1} alignItems="center">
				<Skeleton height={80} sx={{flex: 1, transform: 'initial'}} />
				{!isMobile ? <Skeleton height={80} sx={{flex: 1, transform: 'initial'}} /> : null}
			</Stack>
		);
	}

	return (
		<Stack direction="row" gap={2} flex={1} alignItems="stretch">
			<AutoPaymentCard
				type={autoPaymentType}
				onEnable={onAutoPaymentEnable}
				onChange={onAutoPaymentChange}
				onDeactivate={onAutoPaymentDeactivate}
				isEnableLoading={isAutoPaymentLoading}
				isChangeLoading={isAutoPaymentLoading}
				isDeactivateLoading={isAutoPaymentLoading}
			/>
			{!isMobile ? (
				<StyledCard.Container sx={{flex: 1}}>
					<Stack gap={1} width="100%">
						<Typography variant="caption" color="text.secondary">
							{totalSelectedBills}
						</Typography>
						<Stack direction="row" alignItems="center" justifyContent="space-between">
							<Stack direction="row" alignItems="center" component="span">
								<Typography variant="h6" fontWeight={600} component="span">
									{totalSelectedFormatted}
								</Typography>
								{showAsterisk ? (
									<Typography
										variant="h6"
										component="span"
										color={palette.error.main}
										fontWeight={600}>
										*
									</Typography>
								) : null}
							</Stack>
							<Button
								variant="contained"
								disabled={isPayLoading}
								endIcon={
									isPayLoading ? <Loading size={16} color="inherit" /> : null
								}
								onClick={onPay}>
								{pay}
							</Button>
						</Stack>
					</Stack>
				</StyledCard.Container>
			) : null}
		</Stack>
	);
}

export {PaymentSummaryCards};
